import { ReactElement } from 'react';
import styles from './results.module.scss';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import RaceResultPaginatedView from '@utmb/types/view/RaceResultPaginatedView';
import { useUser } from '@utmb/contexts/AuthContext';
import { DisplayListResult } from '@utmb/design-system/display-list-result/DisplayListResult';
import { MyTable } from '@utmb/design-system/my-table/MyTable';
import { MyTableHeader } from '@utmb/design-system/my-table/MyTableHeader';
import { MyTableCell } from '@utmb/design-system/my-table/MyTableCell';
import RaceResultView from '@utmb/types/view/RaceResultView';
import { MyTableRow } from '@utmb/design-system/my-table/MyTableRow';
import { Skeleton } from '@utmb/design-system/placeholder/Placeholder';
import { Link } from '@utmb/design-system/link/link';
import { Flag } from '@utmb/design-system/flag/Flag';
import { getGender } from '@utmb/utils/gender';
import { Pagination } from '@utmb/design-system/pagination/Pagination';
import { Select } from '@utmb/design-system/select/Select';
import { ListboxOption } from '@reach/listbox';

interface ResultsTableProps {
    isLoading: boolean;
    results: RaceResultPaginatedView;
}

export const ResultsTable = (props: ResultsTableProps): ReactElement => {
    const { results, isLoading } = props;
    const router = useRouter();
    const { offset, limit = 0, nbHits } = results;

    const { locale, push, query, pathname } = useRouter();

    const { login, isAuthenticated } = useUser();

    const { t } = useTranslation();

    let currentPage = offset / limit;

    if (isNaN(currentPage)) {
        currentPage = 0;
    }

    const totalPages = Math.ceil(nbHits / limit);

    const handlePageChange = async (event: { selected: number }): Promise<void> => {
        const page = event.selected + 1;
        if (page === 1) {
            delete router.query.offset;
            delete router.query.limit;
            push({ pathname, query: { ...query, page } }, undefined, { shallow: true });
        } else {
            push({ pathname, query: { ...query, page, offset, limit } }, undefined, { shallow: true });
        }
    };

    const handleGenderFilter = async (gender: string): Promise<void> => {
        push({ pathname, query: { ...query, page: 1, gender } }, undefined, { shallow: true });
    };

    const hrefBuilder = (page: number, pageCount: number) => (page >= 1 && page <= pageCount ? `/` + pathname + `/${query.race}?page=${page}` : '#');

    return (
        <section className={`${styles.rt_root} container g-0`}>
            <div className={styles.rt_table_container}>
                <DisplayListResult label={t('raceRanking.tableName')} hits={nbHits} />

                <MyTable className={styles.rt_table}>
                    <MyTableHeader className={[styles.header_general].filter(Boolean).join(' ')} height={65}>
                        <MyTableCell column={1}>{t('raceRanking.tableHeader.rank')}</MyTableCell>
                        <MyTableCell column={3}>{t('raceRanking.tableHeader.name')}</MyTableCell>
                        <MyTableCell column={2} hiddenOnMobile>
                            {t('raceRanking.tableHeader.nationality')}
                        </MyTableCell>
                        <MyTableCell column={1} className={styles.filter}>
                            <Select
                                id="genderFilter"
                                name="genderFilter"
                                label=""
                                firstOptionlabel={t('common.gender.label')}
                                onchange={(value: string) => {
                                    if (value === 'default') value = '';
                                    handleGenderFilter(value);
                                }}
                                value={query.gender?.toString() ?? ''}
                            >
                                <ListboxOption key={`genderFemale`} value="F" label={t(getGender('F'))}>
                                    {t(getGender('F'))}
                                </ListboxOption>
                                <ListboxOption key={`genderMale`} value="H" label={t(getGender('H'))}>
                                    {t(getGender('H'))}
                                </ListboxOption>
                            </Select>
                        </MyTableCell>
                        <MyTableCell column={2} hiddenOnMobile>
                            {t('raceRanking.tableHeader.ageGroup')}
                        </MyTableCell>
                        <MyTableCell column={1} hiddenOnMobile>
                            {t('raceRanking.tableHeader.time')}
                        </MyTableCell>
                        <MyTableCell column={2} hiddenOnMobile>
                            {t('raceRanking.tableHeader.index')}
                        </MyTableCell>
                    </MyTableHeader>

                    {results?.results?.map((result: RaceResultView, indexKey: number) => {
                        let index = result.index?.toString();

                        if (isAuthenticated) {
                            if (result.rank === null) index = '-';
                            else if (index === '0') {
                                index = t('singleRunner.resultsTable.indexWaiting');
                            }
                        } else {
                            index = t('singleRunner.resultsTable.connectSeeIndex');
                        }

                        return (
                            <MyTableRow key={indexKey} height={50}>
                                <MyTableCell column={1}>{isLoading ? <Skeleton width="50%" /> : `${result.rank ?? 'DNF'}`}</MyTableCell>
                                <MyTableCell column={3}>
                                    {isLoading ? (
                                        <Skeleton width="50%" />
                                    ) : (
                                        <Link href={`https://utmb.world/${locale}/runner/${result.runnerUri}`} target="_blank">
                                            {result.fullname}
                                        </Link>
                                    )}
                                </MyTableCell>
                                <MyTableCell column={2} hiddenOnMobile>
                                    {isLoading ? (
                                        <>
                                            <Skeleton width="15%" height="2.8rem" /> <Skeleton width="40%" />
                                        </>
                                    ) : (
                                        <>
                                            <Flag isoCode={result.nationalityCode} /> <span style={{ marginLeft: '8px' }}>{result.nationality}</span>
                                        </>
                                    )}
                                </MyTableCell>
                                <MyTableCell column={1}>
                                    {isLoading ? <Skeleton width="50%" /> : <span style={{ textTransform: 'capitalize' }}>{t(getGender(result.gender))}</span>}
                                </MyTableCell>
                                <MyTableCell column={2} hiddenOnMobile>
                                    {isLoading ? <Skeleton width="40%" /> : <>{result.ageGroup}</>}
                                </MyTableCell>
                                <MyTableCell column={1} hiddenOnMobile>
                                    {isLoading ? <Skeleton width="70%" /> : `${result.time ?? '-'}`}
                                </MyTableCell>
                                <MyTableCell column={2} hiddenOnMobile>
                                    {isLoading ? (
                                        <Skeleton width="60%" />
                                    ) : isAuthenticated ? (
                                        index
                                    ) : (
                                        <button className={styles.rt_login_button} onClick={login}>
                                            {index}
                                        </button>
                                    )}
                                </MyTableCell>
                            </MyTableRow>
                        );
                    })}
                </MyTable>
            </div>
            {totalPages > 1 && <Pagination forcePage={currentPage} PageCount={totalPages} onPageChange={handlePageChange} hrefBuilder={hrefBuilder} />}
        </section>
    );
};
